import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type { EnterpriseTokenRequestParams } from 'owa-tokenprovider-utils';
import { getUserTokenIfAvailable } from './getUserTokenIfAvailable';
import sleep from 'owa-sleep';
import { getOrigin } from 'owa-url/lib/getOrigin';
import type { LogStartPerformanceUsage } from 'owa-analytics-start';
import { getResourceToken } from './getResourceToken';
import { TokenResponseType } from 'owa-tokenprovider-utils/lib/TokenResponseType';
import { recordRetryAttempt, recordRetryFailure } from './recordRetryTelemetry';

const TELEMETRY_PREFIX = 'FetchFromHostAppEnterprise';

export async function fetchTokenFromHostAppForEnterpriseWithRetry(
    perfLog: LogStartPerformanceUsage,
    retryCount: number,
    retryDelay: number,
    enterpriseParams: EnterpriseTokenRequestParams,
    attemptIndex: number = 1,
    backoffAccumulatedMs: number = 0
): Promise<TokenResponse> {
    if (retryCount < 1) {
        const error = new Error('Max retrying limit (1) reached');
        recordRetryFailure(
            perfLog,
            TELEMETRY_PREFIX,
            error,
            attemptIndex - 1,
            backoffAccumulatedMs
        );
        return Promise.reject(error);
    }

    return fetchTokenFromHostAppForEnterprise(perfLog, enterpriseParams).then(
        result => {
            recordRetryAttempt(perfLog, TELEMETRY_PREFIX, attemptIndex, backoffAccumulatedMs);
            return result;
        },
        error => {
            // Retry only if attempts remain. On the final attempt, reject with the real error.
            if (retryCount > 1) {
                const sleepStart = Date.now();
                return sleep(retryDelay).then(() =>
                    fetchTokenFromHostAppForEnterpriseWithRetry(
                        perfLog,
                        retryCount - 1,
                        retryDelay * 2,
                        { ...enterpriseParams },
                        attemptIndex + 1,
                        backoffAccumulatedMs + Math.max(0, Date.now() - sleepStart)
                    )
                );
            }

            recordRetryFailure(
                perfLog,
                TELEMETRY_PREFIX,
                error,
                attemptIndex,
                backoffAccumulatedMs
            );
            return Promise.reject(error);
        }
    );
}

export async function fetchTokenFromHostAppForEnterprise(
    perfLog: LogStartPerformanceUsage,
    enterpriseParams: EnterpriseTokenRequestParams
): Promise<TokenResponse> {
    const origin = getOrigin();

    if (enterpriseParams.resource == origin || enterpriseParams.resource == origin + '/') {
        const authTokenResponse = await getUserTokenIfAvailable(enterpriseParams);
        if (authTokenResponse?.AccessToken) {
            perfLog.addCheckpoint('UserTokenFetched');
            return authTokenResponse;
        }
    }

    perfLog.addCheckpoint('TokenRetrievalFromHostApp_Start');
    const bridgeStart = Date.now();
    let token: TokenResponse | undefined;
    try {
        token = await getResourceToken(
            enterpriseParams.resource,
            enterpriseParams.wwwAuthenticateHeader,
            enterpriseParams.mailboxInfo,
            enterpriseParams.scope,
            enterpriseParams.requestId,
            enterpriseParams.claims
        );
    } finally {
        perfLog.addCustomProperty('HostBridgeMs', Math.max(0, Date.now() - bridgeStart));
    }

    if (token) {
        perfLog.addCheckpoint('TokenRetrievalFromHostApp_End');

        if (token?.AccessToken) {
            perfLog.addCustomProperty('HostBridgeStatus', 'success');
            return token;
        }

        // Do not fallback if we got back a SubError from OneAuth as this will cause failure in GATFR call as well
        if (token?.SubErrorCode == TokenResponseType.InteractionRequired) {
            perfLog.addCustomProperty('HostBridgeStatus', 'InteractionRequiredError');
        }

        if (!!token?.SubErrorCode) {
            return token;
        }
    }
    return {};
}
